import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PrefetchLink } from "@/components/PrefetchLink";
import { proxyImage } from "@/lib/imageProxy";
import { formatDonation } from "@/lib/utils";
import { Store, ExternalLink, Heart } from "lucide-react";

export interface BrandCardBrand {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  is_active: boolean | null;
  is_featured?: boolean | null;
  donation_percentage: number | null;
  commission_type?: string | null;
}

interface BrandCardProps {
  brand: BrandCardBrand;
  showFeaturedBadge?: boolean;
}

export const BrandCard = ({ brand, showFeaturedBadge = true }: BrandCardProps) => {
  return (
    <Card className="group overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full">
      <CardContent className="p-0 h-full">
        <PrefetchLink to={`/marca/${brand.slug}`} className="block h-full">
          <div className="aspect-[4/3] flex items-center justify-center p-6 relative overflow-hidden bg-muted/30">
            {brand.logo_url ? (
              <img
                src={proxyImage(brand.logo_url, { w: 320 }) ?? brand.logo_url}
                alt={brand.name}
                className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <Store className="w-16 h-16 text-muted-foreground" />
            )}
            {showFeaturedBadge && brand.is_featured && (
              <div className="absolute top-2 right-2 bg-primary text-primary-foreground text-xs font-medium px-2 py-1 rounded-full">
                Destaque
              </div>
            )}
          </div>
          <div className="p-4 border-t">
            <h2 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
              {brand.name}
            </h2>
            {brand.donation_percentage && (
              <div className="flex items-center gap-1 mt-2 text-sm text-success">
                <Heart className="w-4 h-4 fill-current" />
                <span>Até {formatDonation(brand.donation_percentage, brand.commission_type)} de doação</span>
              </div>
            )}
            <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
              <ExternalLink className="w-3 h-3" />
              <span>Ver detalhes</span>
            </div>
          </div>
        </PrefetchLink>
      </CardContent>
    </Card>
  );
};

export const BrandCardSkeleton = () => (
  <Card className="overflow-hidden h-full">
    <CardContent className="p-0 h-full">
      <div className="aspect-[4/3] bg-muted/30 flex items-center justify-center">
        <Skeleton className="w-24 h-24" />
      </div>
      <div className="p-4 border-t">
        <Skeleton className="h-5 w-3/4 mb-2" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </CardContent>
  </Card>
);
